import { Box, styled } from '@mui/material';
import classnames from 'classnames';
import { ReactElement } from 'react';

import { ENOPOrganType, HeartIcon, LiverIcon, LungsIcon } from '#shared';

const organIcon: Record<ENOPOrganType, ReactElement> = {
  Liver: <LiverIcon />,
  Heart: <HeartIcon />,
  Lungs: <LungsIcon />,
};

const PREFIX = 'TSMED-HubsReport-TableHeader';

const tableHeaderClasses = {
  header: `${PREFIX}-header`,
  hubs: `${PREFIX}-hubs`,
  primary: `${PREFIX}-primary`,
  secondary: `${PREFIX}-secondary`,
  organGroup: `${PREFIX}-organGroup`,
  organGroupTitle: `${PREFIX}-organGroupTitle`,
  daysGroup: `${PREFIX}-daysGroup`,
  dayGroup: `${PREFIX}-dayGroup`,
  today: `${PREFIX}-today`,
  tomorrow: `${PREFIX}-tomorrow`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  subheader: `${PREFIX}-subheader`,
};

const TableHeaderStyled = styled(Box)(
  ({ theme: { spacing, mixins, palette, utils } }) => ({
    ...mixins.organStyles(palette, {
      primaryOpacity: 0.9,
      secondaryOpacity: 0.7,
    }),
    display: 'grid',
    width: '100%',
    // Grid: region + hub + 3 organs = 1 + 1 + 3 columns
    gridTemplateColumns: `var(--region-column-width) var(
      --hub-column-width
    ) repeat(3, 1fr)`,
    position: 'sticky',
    top: 0,
    zIndex: 'var(--z-table-header)',
    background: 'black',
    [`.${tableHeaderClasses.hubs}`]: {
      display: 'flex',
      flexDirection: 'column',
      padding: spacing(1),
      justifyContent: 'center',
      gridColumn: '1 / 3',
      position: 'sticky',
      left: 0,
      paddingLeft: 35,
      background: 'black',
      [`.${tableHeaderClasses.primary}`]: {
        fontSize: utils.clamp(16, 32),
      },
      [`.${tableHeaderClasses.secondary}`]: {
        color: palette.grey[400],
        fontSize: utils.clamp(10, 14),
      },
    },

    [`.${tableHeaderClasses.organGroup}`]: {
      display: 'flex',
      flexDirection: 'column',
      color: 'black',
      borderLeft: '2px solid black',
      [`.${tableHeaderClasses.organGroupTitle}`]: {
        fontSize: utils.clamp(14, 20),
        fontWeight: 700,
      },
      [`.${tableHeaderClasses.daysGroup}`]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        height: '100%',
        [`.${tableHeaderClasses.dayGroup}`]: {
          display: 'grid',
          gridTemplateRows: 'auto auto auto',
          alignContent: 'end',
          [`&.${tableHeaderClasses.today}`]: {
            backgroundColor: 'var(--organColor)',
          },
          [`&.${tableHeaderClasses.tomorrow}`]: {
            backgroundColor: 'var(--organSecondaryColor)',
          },
          [`.${tableHeaderClasses.title}`]: {
            padding: spacing(1, 2),
            display: 'flex',
            alignItems: 'center',
            gap: spacing(1),
            svg: {
              width: spacing(3),
              height: spacing(3),
            },
          },

          [`.${tableHeaderClasses.subheader}`]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
            background: 'black',
            color: palette.grey[200],
            display: 'flex',
            flexDirection: 'row',
            [`.${tableHeaderClasses.title}`]: {
              fontSize: utils.clamp(10, 14),
              justifyContent: 'center',
              paddingInline: 0,
              flex: '0 0 33.33%',
            },
          },
        },
      },
    },
  }),
);

export const TableHeader = () => (
  <TableHeaderStyled>
    <Box className={tableHeaderClasses.hubs}>
      <Box className={tableHeaderClasses.primary}>HUB</Box>
      <Box className={tableHeaderClasses.secondary}>Time Zone</Box>
    </Box>
    {[ENOPOrganType.Liver, ENOPOrganType.Heart, ENOPOrganType.Lungs].map(
      organ => (
        <Box key={organ} className={tableHeaderClasses.organGroup}>
          <Box className={tableHeaderClasses.daysGroup}>
            <Box
              className={classnames(
                tableHeaderClasses.dayGroup,
                `organ-type-${organ}`,
                tableHeaderClasses.today,
              )}
            >
              <Box className={tableHeaderClasses.title}>
                {organIcon[organ]} Today
              </Box>
              <Box className={tableHeaderClasses.subheader}>
                <Box className={tableHeaderClasses.title}>CS</Box>
                <Box className={tableHeaderClasses.title}>S</Box>
                <Box className={tableHeaderClasses.title}>OCS</Box>
              </Box>
            </Box>
            <Box
              className={classnames(
                tableHeaderClasses.dayGroup,
                `organ-type-${organ}`,
                tableHeaderClasses.tomorrow,
              )}
            >
              <Box className={tableHeaderClasses.title}>Tomorrow</Box>
              <Box className={tableHeaderClasses.subheader}>
                <Box className={tableHeaderClasses.title}>CS</Box>
                <Box className={tableHeaderClasses.title}>S</Box>
                <Box className={tableHeaderClasses.title}>OCS</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    )}
  </TableHeaderStyled>
);
